<template>
  <div class="filters-content">
    <!-- Filtros rápidos (1 clique): ficam numa linha só, com rolagem horizontal, para não tirar espaço da lista.
         Os demais campos ficam no drawer aberto pela lupa (drawerVisible) -->
    <div v-show="showInline" class="filters-quick">
      <div class="filters-quick-scroll" @wheel="onQuickWheel">
        <b-badge
          pill
          variant="light-secondary"
          class="cursor-pointer filters-pills noselect"
          :class="{ 'badge-light-danger': important }"
          @click.stop="onSelectImportant"
        >
          <feather-icon icon="FlagIcon" class="mr-25" />
          {{ $t('MANAGE_NEWS_FILTER.Important') }}
        </b-badge>
        <b-badge
          pill
          variant="light-secondary"
          class="cursor-pointer filters-pills noselect"
          :class="{ 'badge-light-danger': inLive }"
          @click.stop="onSelectInLive"
        >
          <feather-icon icon="DiscIcon" class="mr-25" />
          {{ $t('MANAGE_NEWS_FILTER.InLive') }}
        </b-badge>
        <b-badge
          pill
          variant="light-secondary"
          class="cursor-pointer filters-pills noselect"
          :class="{ 'badge-light-warning': exclusive }"
          @click="onSelectExclusive"
        >
          <feather-icon icon="BookmarkIcon" class="mr-25" />
          {{ $t('MANAGE_NEWS_FILTER.Exclusive') }}
        </b-badge>
        <b-badge
          pill
          variant="light-secondary"
          class="cursor-pointer filters-pills noselect"
          :class="{ 'badge-light-warning': flashes }"
          @click="onSelectFlashes"
        >
          <feather-icon icon="ZapIcon" class="mr-25" />
          {{ $t('MANAGE_NEWS_FILTER.Flashes') }}
        </b-badge>
        <b-badge
          pill
          variant="light-secondary"
          class="cursor-pointer filters-pills noselect"
          :class="{ 'badge-light-success': highlight }"
          @click="onSelectHighlights"
        >
          <feather-icon icon="AlertTriangleIcon" class="mr-25" />
          {{ $t('MANAGE_NEWS_FILTER.Highlights') }}
        </b-badge>
        <b-badge
          pill
          variant="light-secondary"
          class="cursor-pointer filters-pills noselect"
          :class="[badgeActiveClass]"
          @click="onSelectActive"
        >
          <feather-icon icon="CircleIcon" class="mr-25" />
          <!-- Os 3 rótulos ocupam o mesmo espaço e só o atual aparece: a largura fica fixa no maior (ex.: "Desativado")
               e o filtro não muda de tamanho nem faz a linha de filtros quebrar diferente a cada clique -->
          <span class="filters-pills-fixed-label">
            <span
              v-for="(label, index) in activeLabels"
              :key="index"
              :class="{ 'filters-pills-fixed-label-hidden': label !== activeLabel }"
            >{{ label }}</span>
          </span>
        </b-badge>
        <b-badge
          pill
          variant="light-secondary"
          class="cursor-pointer filters-pills noselect"
          :class="{ 'badge-light-success': mostReadDay }"
          @click="onSelectMostReadDay"
        >
          <feather-icon icon="TrendingUpIcon" class="mr-25" />
          {{ $t('MANAGE_NEWS_FILTER.MostReadDay') }}
        </b-badge>
        <b-badge
          pill
          variant="light-secondary"
          class="cursor-pointer filters-pills noselect"
          :class="{ 'badge-light-success': mostReadWeek }"
          @click="onSelectMostReadWeek"
        >
          <feather-icon icon="TrendingUpIcon" class="mr-25" />
          {{ $t('MANAGE_NEWS_FILTER.MostReadWeek') }}
        </b-badge>
        <b-badge
          pill
          variant="light-secondary"
          class="cursor-pointer filters-pills noselect"
          :class="{ 'badge-light-success': mostReadMonth }"
          @click="onSelectMostReadMonth"
        >
          <feather-icon icon="TrendingUpIcon" class="mr-25" />
          {{ $t('MANAGE_NEWS_FILTER.MostReadMonth') }}
        </b-badge>
      </div>
      <b-button
        v-ripple.400="'rgba(40, 199, 111, 0.15)'"
        variant="flat-danger"
        class="btn-icon btn-clear"
        @click="onClear"
        pill
        size="sm"
      >
        {{ $t('MANAGE_NEWS_FILTER.Clear') }}
      </b-button>
    </div>

    <!-- Campos do drawer que estão aplicados: mostra o que filtra a lista sem precisar abrir o drawer -->
    <div v-show="showInline" v-if="activeChips.length" class="filters-active">
      <b-badge
        v-for="chip in activeChips"
        :key="chip.field"
        pill
        variant="light-primary"
        class="filters-active-chip cursor-pointer"
        :title="`${chip.label}: ${chip.value}`"
        @click="onDrawerChange(true)"
      >
        <span class="filters-active-chip-label">{{ chip.label }}:</span>
        <span class="filters-active-chip-value">{{ chip.value }}</span>
        <feather-icon
          icon="XIcon"
          size="12"
          class="filters-active-chip-remove"
          @click.stop="onRemoveChip(chip.field)"
        />
      </b-badge>
    </div>

    <!-- no-close-on-route-change: cada filtro aplicado atualiza a query da URL (updateRouteQuery), o que fecharia o drawer.
         O backdrop só escurece a tela para destacar o drawer: o CSS deixa os cliques passarem (o clique fora fecha pelo onDocumentMousedown) -->
    <b-sidebar
      :visible="drawerVisible"
      @change="onDrawerChange"
      right
      shadow
      backdrop
      no-enforce-focus
      no-close-on-route-change
      bg-variant="white"
      sidebar-class="manage-news-filter-drawer"
    >
      <template #header="{ hide }">
        <div class="manage-news-filter-drawer-header">
          <span>
            <feather-icon icon="FilterIcon" size="18" class="manage-news-filter-drawer-icon mr-50" />
            {{ $t('MANAGE_NEWS_FILTER.Filters') }}
          </span>
          <feather-icon icon="XIcon" size="18" class="cursor-pointer" @click="hide" />
        </div>
      </template>

      <div class="manage-news-filter-drawer-body">
        <b-form-group :label="$t('MANAGE_NEWS_FILTER.Origin')" label-for="origin-list">
          <v-select
            id="origin-list"
            v-model="originModel"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="name"
            :placeholder="$t('MANAGE_NEWS_FILTER.Origin')"
            :options="filterData.origins"
            clearable
          >
            <span slot="no-options">{{ $t("GENERAL.NoOptions") }}</span>
          </v-select>
        </b-form-group>

        <b-form-group :label="$t('MANAGE_NEWS_FILTER.Subject')" label-for="subject-list">
          <v-select
            id="subject-list"
            v-model="subjectModel"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="name"
            :options="filterData.subjects"
            :placeholder="$t('MANAGE_NEWS_FILTER.Subject')"
            clearable
          >
            <span slot="no-options">{{ $t("GENERAL.NoOptions") }}</span>
          </v-select>
        </b-form-group>

        <b-form-group :label="$t('MANAGE_NEWS_FILTER.User')" label-for="user-list">
          <v-select
            id="user-list"
            v-model="userModel"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="name"
            class="cb-filters"
            :options="filterData.users"
            :placeholder="$t('MANAGE_NEWS_FILTER.User')"
            clearable
          >
            <span slot="no-options">{{ $t("GENERAL.NoOptions") }}</span>
          </v-select>
        </b-form-group>

        <b-form-group :label="$t('MANAGE_NEWS_FILTER.Period')">
          <flat-pickr
            :key="`news-period-${$i18n.locale}`"
            v-model="dateRangeModel"
            class="form-control form-date"
            :placeholder="$t('MANAGE_NEWS_FILTER.Period')"
            :config="{
              enableTime: false,
              dateFormat: 'Y-m-d',
              altInput: true,
              altFormat: 'd/m/Y',
              allowInput: true,
              mode: 'range',
            }"
          />
        </b-form-group>

        <b-form-group :label="$t('MANAGE_CLIENTS_FILTER.DtCreate')">
          <flat-pickr
            :key="`news-dt-create-${$i18n.locale}`"
            v-model="dtCreateModel"
            class="form-control form-date"
            :placeholder="$t('MANAGE_CLIENTS_FILTER.DtCreate')"
            :config="{
              enableTime: false,
              dateFormat: 'Y-m-d',
              altInput: true,
              altFormat: 'd/m/Y',
              allowInput: true,
              mode: 'range',
            }"
          />
        </b-form-group>

        <b-form-group :label="$t('MANAGE_CLIENTS_FILTER.DtReport')">
          <flat-pickr
            :key="`news-dt-report-${$i18n.locale}`"
            v-model="dtReportModel"
            class="form-control form-date"
            :placeholder="$t('MANAGE_CLIENTS_FILTER.DtReport')"
            :config="{
              enableTime: false,
              dateFormat: 'Y-m-d',
              altInput: true,
              altFormat: 'd/m/Y',
              allowInput: true,
              mode: 'range',
            }"
          />
        </b-form-group>

        <b-form-group :label="$t('MANAGE_NEWS_FILTER.NewsId')" label-for="news-id-filter">
          <b-form-input
            id="news-id-filter"
            :placeholder="$t('MANAGE_NEWS_FILTER.NewsId')"
            v-model="newsIdModel"
          />
        </b-form-group>
      </div>

      <template #footer>
        <div class="manage-news-filter-drawer-footer">
          <span class="manage-news-filter-drawer-total">
            {{ $t('MANAGE_NEWS_FILTER.Results', { count: formattedTotalRows }) }}
          </span>
          <div>
            <b-button variant="flat-danger" size="sm" @click="onClear">
              {{ $t('MANAGE_NEWS_FILTER.Clear') }}
            </b-button>
            <b-button variant="primary" size="sm" class="ml-50" @click="onDrawerChange(false)">
              {{ $t('MANAGE_NEWS_FILTER.Close') }}
            </b-button>
          </div>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import { BBadge, BButton, BFormGroup, BFormInput, BSidebar } from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import { ref, getCurrentInstance, watch, onMounted } from "@vue/composition-api";
import Ripple from "vue-ripple-directive";
import AppConstants from '@/appConstants';
// import store from "@/store";

let that;

export default {
  directives: {
    Ripple,
  },
  components: {
    BButton,
    BBadge,
    BFormGroup,
    flatPickr,
    vSelect,
    BFormInput,
    BSidebar
  },
  props: {
    filterData: {
      type: Object,
    },
    totalRows: {
      type: Number,
    },
    selectedFilter: {
      type: Object,
    },
    // Drawer com os campos (origem, assuntos, datas...); quem abre é a lupa do ManageNews
    drawerVisible: {
      type: Boolean,
      default: false,
    },
    // Filtros rápidos e chips na lista (o ManageNews esconde no calendário, onde a lista não aparece)
    showInline: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    stateFilter() {
      return this.$store.state.manageNews.filter;
    },
    // Rótulos possíveis do filtro de status (mesmas chaves usadas em syncActiveLabel/onSelectActive)
    activeLabels() {
      return [
        this.$t('MANAGE_NEWS_FILTER.All'),
        this.$t('MANAGE_NEWS_FILTER.Active'),
        this.$t('MANAGE_NEWS_FILTER.Disable'),
      ];
    },
    formattedTotalRows() {
      return Number(this.totalRows || 0).toLocaleString(this.$i18n.locale);
    },
    // Um chip por campo do drawer que está preenchido (os filtros rápidos já mostram o estado na própria pílula)
    activeChips() {
      const chips = [
        { field: 'origin', label: this.$t('MANAGE_NEWS_FILTER.Origin'), value: this.originModel && this.originModel.name },
        { field: 'subject', label: this.$t('MANAGE_NEWS_FILTER.Subject'), value: this.subjectModel && this.subjectModel.name },
        { field: 'user', label: this.$t('MANAGE_NEWS_FILTER.User'), value: this.userModel && this.userModel.name },
        { field: 'dt_range', label: this.$t('MANAGE_NEWS_FILTER.Period'), value: this.formatChipDate(this.dateRangeModel) },
        { field: 'dt_create', label: this.$t('MANAGE_CLIENTS_FILTER.DtCreate'), value: this.formatChipDate(this.dtCreateModel) },
        { field: 'dt_report', label: this.$t('MANAGE_CLIENTS_FILTER.DtReport'), value: this.formatChipDate(this.dtReportModel) },
        { field: 'newsId', label: this.$t('MANAGE_NEWS_FILTER.NewsId'), value: this.newsIdModel },
      ];
      return chips.filter((chip) => chip.value);
    },
  },
  watch: {
    // Fecha o drawer no clique fora dele; o listener só existe enquanto o drawer está aberto
    drawerVisible(visible) {
      if (visible) {
        document.addEventListener('mousedown', this.onDocumentMousedown, true);
      } else {
        document.removeEventListener('mousedown', this.onDocumentMousedown, true);
      }
    },
    dateRangeModel(val) {
      if (this.isClearData || this.isRestoringFilter) return;
      this.updateStateFilter('dt_range', val);
      console.log("dateRangeModel: ", val);
      this.currentFilter.dt_range = val;
      this.emitFilter();
    },
    dtCreateModel(val) {
      if (this.isClearData || this.isRestoringFilter) return;
      this.updateStateFilter('dt_create', val);
      console.log("dtCreateModel: ", val);
      this.currentFilter.dt_create = val;
      this.emitFilter();
    },
    dtReportModel(val) {
      if (this.isClearData || this.isRestoringFilter) return;
      this.updateStateFilter('dt_report', val);
      console.log("dtReportModel: ", val);
      this.currentFilter.dt_report = val;
      this.emitFilter();
    },
    toDateModel(val) {
      if (this.isClearData || this.isRestoringFilter) return;
      this.updateStateFilter('dt_end', val);
      this.currentFilter.dt_end = val;
      this.emitFilter();
    },
    subjectModel(val) {
      if (this.isClearData || this.isRestoringFilter) return;
      this.updateStateFilter('subject', val);
      this.currentFilter.subject = val;
      this.emitFilter();
    },
    userModel(val) {
      if (this.isClearData || this.isRestoringFilter) return;
      this.updateStateFilter('user', val);
      this.currentFilter.user = val;
      this.emitFilter();
    },
    originModel(val) {
      if (this.isClearData || this.isRestoringFilter) return;
      this.updateStateFilter('origin', val);
      this.currentFilter.origin = val;
      this.emitFilter();
    },
    newsIdModel(val) {
      if (this.isClearData || this.isRestoringFilter) return;
      this.updateStateFilter('newsId', val);
      this.currentFilter.newsId = val;
      this.emitFilter();
    },
  },
  created() {
    //   console.log('created popover')
    //   console.log('filterData: ', this.filterData)
    that = this;
    // Só restaura a tela com os filtros da URL: o ManageNews já levou esses valores para o store e já buscou
    this.isRestoringFilter = true;
    this.$nextTick(() => {
      this.isRestoringFilter = false;
    });
    if (this.selectedFilter) {
      if (Object.keys(this.selectedFilter).length > 0) {
        Object.keys(this.selectedFilter).forEach((item) => {
          // console.log(this.selectedFilter[item].id)
          if (item == "id") {
            that.newsIdModel = that.filterData.newsId.find((item) => {
              return item == that.selectedFilter.newsId;
            });
            that.currentFilter.newsId = that.newsIdModel;
          }
          if (item == "origin") {
            that.originModel = that.filterData.origins.find((item) => {
              return item.id == that.selectedFilter.origin.id;
            });
            that.currentFilter.origin = that.originModel;
          }
          if (item == "user") {
            that.userModel = that.filterData.users.find((item) => {
              return item.id == that.selectedFilter.user.id;
            });
            that.currentFilter.user = that.userModel;
          }
          if (item == "subject") {
            that.subjectModel = that.filterData.subjects.find((item) => {
              return item.id == that.selectedFilter.subject.id;
            });
            that.currentFilter.subject = that.subjectModel;
          }
          if (item == "dt_range") {
            that.dateRangeModel = that.selectedFilter[item];
            that.currentFilter.dt_range = that.dateRangeModel;
          }
          if (item == "dt_create") {
            that.dtCreateModel = that.selectedFilter[item];
            that.currentFilter.dt_create = that.dtCreateModel;
          }
          if (item == "dt_report") {
            that.dtReportModel = that.selectedFilter[item];
            that.currentFilter.dt_report = that.dtReportModel;
          }
          if (item == "dt_end") {
            that.toDateModel = that.selectedFilter[item];
            that.currentFilter.dt_end = that.toDateModel;
          }
          if (item == "important") {
            that.important = that.selectedFilter[item];
          }
          if (item == "inLive") {
            that.inLive = that.selectedFilter[item];
          }
          if (item == "flashes") {
            that.flashes = that.selectedFilter[item];
          }
          if (item == "highlight") {
            that.highlight = that.selectedFilter[item];
          }
          if (item == "mostReadDay") {
            that.mostReadDay = that.selectedFilter[item];
          }
          if (item == "mostReadWeek") {
            that.mostReadWeek = that.selectedFilter[item];
          }
          if (item == "mostReadMonth") {
            that.mostReadMonth = that.selectedFilter[item];
          }
          if (item == "exclusive") {
            that.exclusive = that.selectedFilter[item];
          }
          if (item == "active") {
            that.active = that.selectedFilter[item];
          }
        });
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown, true);
  },
  methods: {
    onDrawerChange(visible) {
      this.$emit("drawer-change", visible);
    },
    // Sem backdrop: o clique fora fecha o drawer e segue para o alvo (ex.: abrir uma notícia da lista).
    // Ignora o calendário do flatpickr (renderizado no body), a lupa (já alterna o drawer) e os chips (já abrem o drawer)
    onDocumentMousedown(event) {
      const target = event.target;
      const ignore = '.manage-news-filter-drawer, .flatpickr-calendar, .btn-search-collapse, .filters-active-chip';
      if (target && target.closest && target.closest(ignore)) return;
      this.onDrawerChange(false);
    },
    // Limpar o campo dispara o watcher dele, que atualiza o store e refaz a busca
    onRemoveChip(field) {
      const models = {
        origin: 'originModel',
        subject: 'subjectModel',
        user: 'userModel',
        dt_range: 'dateRangeModel',
        dt_create: 'dtCreateModel',
        dt_report: 'dtReportModel',
        newsId: 'newsIdModel',
      };
      this[models[field]] = null;
    },
    // O flatpickr guarda "Y-m-d" (range: duas datas com separador do idioma); no chip fica "d/m/Y – d/m/Y"
    formatChipDate(val) {
      if (!val) return '';
      const dates = String(val).match(/\d{4}-\d{2}-\d{2}/g);
      if (!dates) return String(val);
      return dates.map((date) => date.split('-').reverse().join('/')).join(' – ');
    },
    // A linha de filtros rápidos rola na horizontal; a roda do mouse (vertical) também rola a linha
    onQuickWheel(event) {
      const el = event.currentTarget;
      if (el.scrollWidth <= el.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      el.scrollLeft += event.deltaY;
      event.preventDefault();
    },
    updateStateFilter(field, val) {
      this.$store.dispatch("manageNews/updateFilter", { field: field, value: val });
    },
    onSelectImportant() {
      this.updateStateFilter('important', !this.stateFilter.important);
      this.important = !this.important;
      this.currentFilter.important = this.important;
      this.$forceUpdate();
      this.emitFilter();
    },
    onSelectInLive() {
      this.updateStateFilter('inLive', !this.stateFilter.inLive);
      this.inLive = !this.inLive;
      this.currentFilter.inLive = this.inLive;
      this.$forceUpdate();
      this.emitFilter();
    },
    onSelectFlashes() {
      this.updateStateFilter('flashes', !this.stateFilter.flashes);
      this.flashes = !this.flashes;
      this.currentFilter.flashes = this.flashes;
      this.$forceUpdate();
      this.emitFilter();
    },
    onSelectHighlights() {
      this.updateStateFilter('highlight', !this.stateFilter.highlight);
      this.highlight = !this.highlight;
      this.currentFilter.highlight = this.highlight;
      this.$forceUpdate();
      this.emitFilter();
    },
    onSelectExclusive() {
      this.updateStateFilter('exclusive', !this.stateFilter.exclusive);
      this.exclusive = !this.exclusive;
      this.currentFilter.exclusive = this.exclusive;
      this.$forceUpdate();
      this.emitFilter();
    },
    onSelectMostReadDay() {
      this.updateStateFilter('mostReadDay', !this.stateFilter.mostReadDay);
      this.updateStateFilter('mostReadWeek', false);
      this.updateStateFilter('mostReadMonth', false);
      this.mostReadDay = !this.mostReadDay;
      this.mostReadWeek = false;
      this.mostReadMonth = false;
      this.currentFilter.mostReadDay = this.mostReadDay;
      this.currentFilter.mostReadWeek = this.mostReadWeek;
      this.currentFilter.mostReadMonth = this.mostReadMonth;
      this.$forceUpdate();
      this.emitFilter();
    },
    onSelectMostReadWeek() {
      this.updateStateFilter('mostReadWeek', !this.stateFilter.mostReadWeek);
      this.updateStateFilter('mostReadDay', false);
      this.updateStateFilter('mostReadMonth', false);
      this.mostReadWeek = !this.mostReadWeek;
      this.mostReadDay = false;
      this.mostReadMonth = false;
      this.currentFilter.mostReadWeek = this.mostReadWeek;
      this.currentFilter.mostReadDay = this.mostReadDay;
      this.currentFilter.mostReadMonth = this.mostReadMonth;
      this.$forceUpdate();
      this.emitFilter();
    },
    onSelectMostReadMonth() {
      this.updateStateFilter('mostReadMonth', !this.stateFilter.mostReadMonth);
      this.updateStateFilter('mostReadWeek', false);
      this.updateStateFilter('mostReadDay', false);
      this.mostReadMonth = !this.mostReadMonth;
      this.mostReadDay = false;
      this.mostReadWeek = false;
      this.currentFilter.mostReadMonth = this.mostReadMonth;
      this.currentFilter.mostReadDay = this.mostReadDay;
      this.currentFilter.mostReadWeek = this.mostReadWeek;
      this.$forceUpdate();
      this.emitFilter();
    },
    onSelectActive() {
      if (!this.active) {
        this.active = AppConstants.ACTIVE.ALL;
        this.updateStateFilter('active', AppConstants.ACTIVE.ALL);
      }
      switch (Number(this.active)) {
        case AppConstants.ACTIVE.ALL:
          this.active = AppConstants.ACTIVE.TRUE;
          this.activeLabel = this.$t('MANAGE_NEWS_FILTER.Active');
          this.badgeActiveClass = 'badge-light-success';
          break;
        case AppConstants.ACTIVE.TRUE:
          this.active = AppConstants.ACTIVE.FALSE;
          this.activeLabel = this.$t('MANAGE_NEWS_FILTER.Disable');
          this.badgeActiveClass = 'badge-light-danger';
          break;
        case AppConstants.ACTIVE.FALSE:
          this.active = AppConstants.ACTIVE.ALL;
          this.activeLabel = this.$t('MANAGE_NEWS_FILTER.All');
          this.badgeActiveClass = '';
          break;
      }
      this.updateStateFilter('active', this.active);
      this.currentFilter.active = this.active;
      this.$forceUpdate();
      this.emitFilter();
    },
  },
  setup() {
    const vm = getCurrentInstance();
    const translate = (key) => (vm && vm.proxy ? vm.proxy.$t(key) : key);

    // const dt = new Date();
    // const dateRangeModel = ref(`${dt.getFullYear()}-${dt.getMonth() + 1}-${dt.getDate()}`);
    const dateRangeModel = ref(``);
    const dtCreateModel = ref('');
    const dtReportModel = ref('');
    const toDateModel = ref(null);
    const subjectModel = ref(null);
    const userModel = ref(null);
    const newsIdModel = ref(null);
    const originModel = ref(null);
    const important = ref(false);
    const inLive = ref(false);
    const flashes = ref(false);
    const highlight = ref(false);
    const exclusive = ref(false);
    const mostReadDay = ref(false);
    const mostReadWeek = ref(false);
    const mostReadMonth = ref(false);
    const active = ref(AppConstants.ACTIVE.ALL);
    const activeLabel = ref(translate('MANAGE_NEWS_FILTER.All'));
    const badgeActiveClass = ref('');
    const currentFilter = ref({
      newsId: null,
      origin: null,
      user: null,
      subject: null,
      dt_range: null,
      dt_create: null,
      dt_report: null,
      dt_end: null,
      active: AppConstants.ACTIVE.ALL
    });
    const isClearData = ref(false);
    const isRestoringFilter = ref(false);

    const selectOrigin = (data) => {
      currentFilter.value.origin = data;
      emitFilter();
    };

    const emitFilter = () => {
      //   that.$forceUpdate();
      that.$emit("onFilter", currentFilter.value);
    };

    const resetClearStatus = () => {
      isClearData.value = false;
    }

    const syncActiveLabel = () => {
      switch (Number(active.value)) {
        case AppConstants.ACTIVE.TRUE:
          activeLabel.value = translate('MANAGE_NEWS_FILTER.Active');
          break;
        case AppConstants.ACTIVE.FALSE:
          activeLabel.value = translate('MANAGE_NEWS_FILTER.Disable');
          break;
        default:
          activeLabel.value = translate('MANAGE_NEWS_FILTER.All');
          break;
      }
    };

    watch(() => (vm && vm.proxy ? vm.proxy.$i18n.locale : ''), () => {
      syncActiveLabel();
    });

    onMounted(() => {
      syncActiveLabel();
    });

    const onClear = () => {
      isClearData.value = true;
      // store.dispatch("manageNews/clearFilter");

      // currentFilter.value.event_status = props.filterData.event_status[0];
      currentFilter.value.newsId = null;
      currentFilter.value.origin = null;
      currentFilter.value.user = null;
      currentFilter.value.subject = null;
      currentFilter.value.dt_range = null;
      currentFilter.value.dt_report = null;
      currentFilter.value.dt_create = null;
      currentFilter.value.dt_end = null;
      currentFilter.value.important = null;
      currentFilter.value.inLive = null;
      currentFilter.value.flashes = null;
      currentFilter.value.highlight = null;
      currentFilter.value.exclusive = null;
      currentFilter.value.mostReadDay = null;
      currentFilter.value.mostReadWeek = null;
      currentFilter.value.mostReadMonth = null;
      currentFilter.value.active = null;
      userModel.value = null;
      subjectModel.value = null;
      originModel.value = null;
      dateRangeModel.value = null;
      toDateModel.value = null;
      important.value = null;
      inLive.value = null;
      flashes.value = null;
      highlight.value = null;
      exclusive.value = null;
      mostReadDay.value = null;
      mostReadWeek.value = null;
      mostReadMonth.value = null;
      dtCreateModel.value = null;
      dtReportModel.value = null;
      newsIdModel.value = null;
      active.value = AppConstants.ACTIVE.ALL;
      //   that.$forceUpdate();
      that.$emit("onClear", currentFilter.value);
    };
    return {
      newsIdModel,
      dateRangeModel,
      dtCreateModel,
      dtReportModel,
      toDateModel,
      subjectModel,
      userModel,
      originModel,
      currentFilter,
      important,
      inLive,
      flashes,
      highlight,
      exclusive,
      mostReadDay,
      mostReadWeek,
      mostReadMonth,
      active,
      activeLabel,
      badgeActiveClass,

      selectOrigin,
      emitFilter,
      onClear,

      resetClearStatus,
      isClearData,
      isRestoringFilter
    };
  },
};
</script>

<style></style>
